import { useState, useEffect } from "react";
import { SelectedApp } from "@/components/applications/ApplicationCatalog";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Store, Building2, Search, Users, Tag, AlertCircle, Zap, Lock, Info } from "lucide-react";
import { getAllCustomers, getCustomerName } from "@/utils/customerLookup";
import { getGroupsByCustomerId } from "@/utils/customerGroups";
import { getTagsByCustomerId, CustomerTagItem } from "@/utils/customerTags";
import CustomerTag from "@/components/CustomerTag";

interface AppAssignmentStepProps {
  selectedApp: SelectedApp;
  customers: string[];
  groups: string[];
  tags: string[];
  setCustomers: (customers: string[]) => void;
  setGroups: (groups: string[]) => void;
  setTags: (tags: string[]) => void;
  isAbmApp?: boolean;
  preSelectedCustomer?: { id: string; name: string } | null;
}

export const AppAssignmentStep = ({ 
  selectedApp, 
  customers,
  groups,
  tags,
  setCustomers, 
  setGroups,
  setTags,
  isAbmApp = false,
  preSelectedCustomer = null
}: AppAssignmentStepProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [availableTags, setAvailableTags] = useState<CustomerTagItem[]>([]);
  const [deviceImpact, setDeviceImpact] = useState<number>(0);

  const appName = selectedApp.app.name;
  const appIcon =
    selectedApp.type === 'appStore' || selectedApp.type === 'abm'
      ? selectedApp.app.icon
      : undefined;
  const appVersion = selectedApp.app.version;
  const appPlatform =
    selectedApp.type === 'appStore' || selectedApp.type === 'abm'
      ? selectedApp.app.platform
      : (selectedApp.app.platform as string | undefined) ?? 'macos';

  // Auto-select customer for ABM apps
  useEffect(() => {
    if (isAbmApp && preSelectedCustomer && !customers.includes(preSelectedCustomer.id)) {
      setCustomers([preSelectedCustomer.id]);
    }
  }, [isAbmApp, preSelectedCustomer, customers, setCustomers]);

  // Update available groups and tags when customers change
  useEffect(() => {
    if (customers.length > 0) {
      const allGroups = customers.flatMap(customerId => 
        getGroupsByCustomerId(customerId)
      );
      const uniqueGroups = allGroups.filter((group, index, self) =>
        index === self.findIndex(g => g.id === group.id)
      );
      setAvailableGroups(uniqueGroups);

      const allTags = customers.flatMap(customerId => 
        getTagsByCustomerId(customerId)
      );
      const uniqueTags = allTags.filter((tag, index, self) =>
        index === self.findIndex(t => t.id === tag.id)
      );
      setAvailableTags(uniqueTags);
    } else {
      setAvailableGroups([]);
      setAvailableTags([]);
    }
  }, [customers]);

  // Calculate device impact
  useEffect(() => {
    let impact = 0;
    if (customers.length > 0) {
      impact += customers.length * 50;
      impact += groups.length * 25;
    }
    setDeviceImpact(Math.floor(impact));
  }, [customers.length, groups.length]);

  const filteredCustomers = getAllCustomers().filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCustomerToggle = (customerId: string, checked: boolean) => {
    if (checked) {
      setCustomers([...customers, customerId]);
    } else {
      setCustomers(customers.filter(id => id !== customerId));
      const customerGroups = getGroupsByCustomerId(customerId);
      const customerTags = getTagsByCustomerId(customerId);
      
      setGroups(groups.filter(groupId => 
        !customerGroups.some(g => g.id === groupId)
      ));
      setTags(tags.filter(tagId => 
        !customerTags.some(t => t.id === tagId)
      ));
    }
  };

  const handleGroupToggle = (groupId: string, checked: boolean) => {
    if (checked) {
      setGroups([...groups, groupId]);
    } else {
      setGroups(groups.filter(id => id !== groupId));
    }
  };

  const handleTagToggle = (tagId: string, checked: boolean) => {
    if (checked) {
      setTags([...tags, tagId]);
    } else {
      setTags(tags.filter(id => id !== tagId));
    }
  };

  const getCustomerName = (id: string) => {
    return getAllCustomers().find(c => c.id === id)?.name || "Unknown";
  };

  const getGroupDepth = (groupId: string): number => {
    const group = [].find(g => g.id === groupId);
    let depth = 0;
    let current = group;
    while (current?.parentId) {
      depth++;
      current = [].find(g => g.id === current?.parentId);
    }
    return depth;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Assignment & Impact</h2>
        <p className="text-muted-foreground">
          Select customers and tags, then review the deployment impact
        </p>
      </div>

      {/* Selected App Summary */}
      <Card disableHover className="max-w-2xl mx-auto">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border">
              <img 
                src={appIcon} 
                alt={appName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg truncate">{appName}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  {selectedApp.type === 'appStore' ? (
                    <><Store className="h-3 w-3 mr-1" />App Store</>
                  ) : (
                    <><Building2 className="h-3 w-3 mr-1" />ABM</>
                  )}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {appPlatform}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  v{appVersion}
                </Badge>
                {selectedApp.type === 'abm' && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedApp.app.licenseTotal - selectedApp.app.licenseUsed} licenses available
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3-Column Selection Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Selection */}
        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Customer{isAbmApp ? ' (Auto-assigned)' : 's'}
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            {isAbmApp && preSelectedCustomer ? (
              <div className="space-y-4">
                {/* Locked customer display for ABM apps */}
                <div className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Lock className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CustomerTag customer={preSelectedCustomer.name} />
                        <span className="font-medium">{preSelectedCustomer.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Linked via ABM connection
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    Customer is automatically determined by the ABM connection this app belongs to.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {filteredCustomers.map((customer) => (
                      <div key={customer.id} className="flex items-center space-x-2 p-2 rounded hover:bg-muted">
                        <Checkbox
                          id={customer.id}
                          checked={customers.includes(customer.id)}
                          onCheckedChange={(checked) => handleCustomerToggle(customer.id, !!checked)}
                        />
                        <Label htmlFor={customer.id} className="cursor-pointer flex items-center gap-2 flex-1">
                          <CustomerTag customer={customer.name} />
                          <span className="text-sm">{customer.name}</span>
                        </Label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </GlassCardContent>
        </GlassCard>

        {/* Groups Selection */}
        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Groups
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            {customers.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select customers first</p>
                </div>
              </div>
            ) : availableGroups.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No groups available</p>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {availableGroups.map((group) => {
                    const depth = getGroupDepth(group.id);
                    return (
                      <div key={group.id} className="flex items-center space-x-2 p-2 rounded hover:bg-muted">
                        <Checkbox
                          id={group.id}
                          checked={groups.includes(group.id)}
                          onCheckedChange={(checked) => handleGroupToggle(group.id, !!checked)}
                        />
                        <Label 
                          htmlFor={group.id} 
                          className="cursor-pointer flex-1 text-sm"
                          style={{ marginLeft: depth * 12 }}
                        >
                          {group.name}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </GlassCardContent>
        </GlassCard>

        {/* Tags Selection */}
        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-base flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Tags
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            {customers.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <div className="text-center">
                  <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select customers first</p>
                </div>
              </div>
            ) : availableTags.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <div className="text-center">
                  <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No tags available</p>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {availableTags.map((tag) => (
                    <div key={tag.id} className="flex items-center space-x-2 p-2 rounded hover:bg-muted">
                      <Checkbox
                        id={tag.id}
                        checked={tags.includes(tag.id)}
                        onCheckedChange={(checked) => handleTagToggle(tag.id, !!checked)}
                      />
                      <div className="flex-1 flex items-center justify-between">
                        <Label htmlFor={tag.id} className="cursor-pointer text-sm">
                          {tag.name}
                        </Label>
                        <Badge 
                          variant={tag.type === "dynamic" ? "secondary" : "outline"} 
                          className="text-xs"
                        >
                          {tag.type}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Deployment Impact */}
      <GlassCard variant="strong" className="border-primary/20 bg-primary/5">
        <GlassCardHeader>
          <GlassCardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Deployment Impact
          </GlassCardTitle>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-1">
                {deviceImpact}
              </div>
              <div className="text-sm text-muted-foreground">
                Estimated devices affected
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-semibold mb-1">
                {customers.length}
              </div>
              <div className="text-sm text-muted-foreground">
                Customer{customers.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-semibold mb-1">
                {groups.length}
              </div>
              <div className="text-sm text-muted-foreground">
                Group{groups.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          
          {deviceImpact > 100 && (
            <div className="mt-4 p-3 bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">
                High impact deployment - this will affect {deviceImpact} devices across {customers.length} customer{customers.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      {/* Assignment Summary */}
      {customers.length > 0 && (
        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-base">Assignment Summary</GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium">Customers ({customers.length}):</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {customers.map(customerId => (
                    <Badge key={customerId} variant="secondary" className="text-xs">
                      {getCustomerName(customerId)}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {groups.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Groups ({groups.length}):</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {groups.map(groupId => {
                      const group = availableGroups.find(g => g.id === groupId);
                      return group ? (
                        <Badge key={groupId} variant="outline" className="text-xs">
                          {group.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {tags.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Tags ({tags.length}):</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map(tagId => {
                      const tag = availableTags.find(t => t.id === tagId);
                      return tag ? (
                        <Badge 
                          key={tagId} 
                          variant={tag.type === "dynamic" ? "secondary" : "outline"} 
                          className="text-xs"
                        >
                          {tag.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}
    </div>
  );
};
